import {
  KatButton, KatIcon, KatLabel, KatLink, KatPopover,
} from '@amzn/katal-react';
import subDays from 'date-fns/subDays';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SearchListingContainer from 'src/components/search-listing/SearchListingContainer';
import { createSearchAPIContract } from 'src/components/search-listing/utils/createSearchAPIContract';
import { DateRangeType, SortByType } from 'src/components/search-listing/utils/types';
import { ButtonTypes } from 'src/constants/metrics';
import { PREFERENCES, THREAD_LISTING } from 'src/constants/pages';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchSearchResults, fetchSearchResultsV2, SearchType } from 'src/redux/slices/postSearch';
import { AppState } from 'src/redux/store';
import { RequestType } from 'src/types/Search';
import { ProfileStatus } from 'src/types/UserInfo';
import { updateConfig, useFirstRender, useMetrics } from 'src/utils';
import LocalStorageKeys from 'src/utils/localStorageKeys';
import { selectMarketplaceId, selectUserPreferences, updateUserPreferences } from 'src/redux/slices/userInfo';
import WeblabId from 'src/weblab/weblabId';
import { selectIsWeblabIdInT1 } from 'src/redux/slices/weblab';
import DropdownWithPreference from '../DropdownWithDefault';
import PinnedPostContainer from '../search-listing/PinnedPostContainer';
import { getSelectedFiltersInitState, getSortOptions } from '../search-listing/SearchListingsSideBar';

const RecentDiscussion: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userId = useAppSelector((state: AppState) => state.userInfo.userId);
  const userPreferences = useAppSelector(selectUserPreferences);
  const profileStatus = useAppSelector(({ userInfo }) => userInfo.profileStatus);
  const userPreferenceStatus = useAppSelector(({ userInfo }) => userInfo.userPreferenceStatus);
  const marketplaceId = useAppSelector(selectMarketplaceId);
  const isRegistered = profileStatus === ProfileStatus.registered;
  const defaultSortBy: SortByType = SortByType.lastActivityTime;
  const allowedSortByTypes = Object.values(SortByType);
  const isPersistedPreference = useAppSelector((state: AppState) => selectIsWeblabIdInT1(
    state,
    WeblabId.SELLER_FORUMS_USER_PREF_STORAGE,
  ));
  const isHomepageSSR = useAppSelector((state: AppState) => selectIsWeblabIdInT1(
    state,
    WeblabId.SELLER_FORUMS_HOMEPAGE_SSR,
  ));
  const defaultSortByFilter = isPersistedPreference
    ? userPreferences.threadListingSortBy
    : getSelectedFiltersInitState(
      isRegistered,
      defaultSortBy,
      allowedSortByTypes,
      userId,
    ).sortBy;
  const [sortBy, setSortBy] = useState<SortByType>(
    defaultSortByFilter,
  );

  const { clickMetric } = useMetrics();
  const firstRender = useFirstRender();
  const { push } = useRouter();
  useEffect(() => {
    if (isHomepageSSR) {
      // make a fetch call only on sortByChanges not on first render
      // since the data is already fetched on the server
      if (!firstRender) {
        dispatch(fetchSearchResultsV2({
          marketplaceId,
          sortBy,
          dateRange: DateRangeType.custom,
          startDate: subDays(new Date(), 7).getTime(),
          endDate: new Date().getTime(),
          pinnedToListing: false,
          pageSize: 10,
        }));
      }
    } else {
      dispatch(fetchSearchResults({
        requestType: RequestType.listing,
        type: SearchType.threadListing,
        request: createSearchAPIContract({
          sortBy,
          requestType: RequestType.listing,
          startDate: subDays(new Date(), 7).getTime(),
          endDate: new Date().getTime(),
          pinnedToListing: false,
        }),
      }));
    }
  }, [sortBy]);

  useEffect(() => {
    if (!isHomepageSSR) {
      dispatch(fetchSearchResults({
        requestType: RequestType.listing,
        type: SearchType.pinnedThreads,
        request: createSearchAPIContract({
          sortBy: SortByType.createdAt,
          requestType: RequestType.listing,
          pinnedToListing: true,
        }),
      }));
    }
  }, []);

  return (
    <div className="container">
      <div className="kat-col-lg-8 kat-offset-lg-2">
        <div className="mb-2.5 flex flex-col">
          <div className="mb-[18px] flex flex-row justify-between">
            <div className="flex flex-row items-center gap-x-1">
              <h3 className="m-0">{t('homePage_recentDiscussionsHeader')}</h3>
              <KatPopover
                variant="tooltip"
                katAriaBehavior="tooltip"
                position="top"
                triggerType="hover"
              >
                <KatIcon slot="trigger" name="help_outline" size="tiny" />
                <div>
                  <Trans
                    i18nKey="homePage_recentDiscussionsHeader_tooltip"
                  />
                </div>
              </KatPopover>
            </div>
            <div className="flex basis-1/4 flex-row items-center gap-x-3">
              <div className="whitespace-nowrap">
                <KatLabel
                  text={t('sort_header')}
                />
              </div>
              <div className="grow">
                <DropdownWithPreference<SortByType>
                  dataTestId="sortby-dropdown"
                  options={getSortOptions(isRegistered)}
                  onChange={(value) => {
                    clickMetric({ metricValue: value, metricName: ButtonTypes.discussionsFilter });
                    setSortBy(value as SortByType);
                  }}
                  value={sortBy}
                  modalTitle={t('preferenceDropdown_sortBy_modal_title')}
                  modalContent={t('preferenceDropdown_sortBy_modal_content')}
                  defaultSelectedOption={defaultSortByFilter}
                  onSaveDefault={(value) => {
                    updateConfig(
                      LocalStorageKeys.discussionListingSortBy,
                      value,
                      userId,
                    );
                    if (isPersistedPreference) {
                      dispatch(updateUserPreferences({
                        sortByPref: value,
                      }));
                    }
                  }}
                  allowDefault={isRegistered}
                  modalFetchStatus={userPreferenceStatus}
                  modalFailureMessage={t('request_genericFailureHeader')}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {
              sortBy === SortByType.relevance && (
              <div className="flex flex-row items-start border border-solid border-kat-seattle bg-kat-snow py-2.5 px-[15px] text-sm" data-testid="preference-update-instruction">
                <KatIcon name="assistant" size="tiny" className="mt-1 flex ltr:mr-2.5 rtl:ml-2.5" />
                <span>
                  <Trans i18nKey="sort_relevance_instruction">
                    <KatLink
                      data-testid="preference-page-link"
                      className="cursor-pointer"
                      onClick={() => {
                        clickMetric({ metricName: ButtonTypes.updatePreferences });
                        push(PREFERENCES);
                      }}
                    />
                  </Trans>
                </span>
              </div>
              )
            }
          </div>
        </div>
        <div className="mb-4">
          <PinnedPostContainer />
        </div>
        <SearchListingContainer filters={{ sortBy }} />
        <div className="my-8 flex flex-row items-center justify-center">
          <KatButton
            label={t('homePage_viewDiscussions')}
            data-testid="view-discussions-link"
            onClick={() => {
              clickMetric({ metricName: ButtonTypes.viewAllDiscussions });
              push(THREAD_LISTING);
            }}
          />
        </div>
      </div>
    </div>
  );
};
export default RecentDiscussion;
